import { useState } from 'react';
import { Share2, Link2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

function FacebookIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
    </svg>
  );
}

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

/**
 * ShareButtons — renders Facebook / WhatsApp / Email / Copy-link share actions.
 *
 * Props:
 *   url   — full URL of the property (defaults to window.location.href)
 *   title — property title or description used in share text
 *   rent  — monthly rent (number)
 *   city  — city name
 *   compact — if true, shows a single "Share" button that expands into a popover
 */
export default function ShareButtons({ url, title, rent, city, compact = false }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;
  const rentStr = rent ? `₹${Number(rent).toLocaleString('en-IN')}/month` : '';
  const shareText = `Check out this rental property${title ? `: ${title}` : ''}${city ? ` in ${city}` : ''}${rentStr ? ` at ${rentStr}` : ''}`;

  const links = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      bg: 'bg-[#25D366] hover:bg-[#20bd5a]',
      icon: <WhatsAppIcon className="w-4 h-4" />,
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
    },
    {
      id: 'facebook',
      label: 'Facebook',
      bg: 'bg-[#1877F2] hover:bg-[#166fe5]',
      icon: <FacebookIcon className="w-4 h-4" />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'email',
      label: 'Email',
      bg: 'bg-slate-700 hover:bg-slate-800',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      href: `mailto:?subject=${encodeURIComponent(`Rental Property: ${title || 'Property on Rent Mitra'}`)}&body=${encodeURIComponent(`${shareText}\n\nView the full listing here:\n${shareUrl}`)}`,
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  /* ── Compact mode: single button + popover ── */
  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition-all text-xs font-medium"
          data-testid="share-compact-btn"
          title="Share property"
        >
          <Share2 className="w-3.5 h-3.5" />
          Share
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute bottom-full mb-2 right-0 z-50 bg-white rounded-2xl shadow-xl border border-slate-100 p-3 min-w-[180px]" data-testid="share-popover">
              <div className="flex items-center justify-between mb-2.5 px-1">
                <span className="text-xs font-semibold text-slate-600">Share Property</span>
                <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-1.5">
                {links.map(l => (
                  <a
                    key={l.id}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2 ${l.bg} text-white rounded-xl text-xs font-semibold transition-colors`}
                    data-testid={`share-${l.id}`}
                  >
                    {l.icon}{l.label}
                  </a>
                ))}
                <button
                  onClick={() => { handleCopy(); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition-colors"
                  data-testid="share-copy"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  /* ── Full mode: inline row of buttons ── */
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-buttons">
      <span className="text-sm font-semibold text-slate-600 flex items-center gap-1.5 mr-1">
        <Share2 className="w-4 h-4" /> Share:
      </span>
      {links.map(l => (
        <a
          key={l.id}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1.5 px-4 py-2 ${l.bg} text-white rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md active:scale-95`}
          data-testid={`share-${l.id}`}
        >
          {l.icon}{l.label}
        </a>
      ))}
      <button
        onClick={handleCopy}
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-semibold transition-all active:scale-95"
        data-testid="share-copy-link"
      >
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4" />}
        {copied ? 'Copied!' : 'Copy Link'}
      </button>
    </div>
  );
}
